import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, ChevronRight, Loader2, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { WarningAlert } from 'components/blocks/warning-alert';
import { Checkbox } from 'components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import {
  useGetComposioToolkitCatalog,
  useGetComposioToolkitSlugs,
  useGetToolById,
  useSaveComposioTool,
} from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type {
  IComposioCatalogToolkit,
  ToolStatus,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { cn } from 'lib/utils';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { DEFAULT_RESOURCE_VISIBILITY, type ResourceVisibility } from 'types/visibility';
import { getToolFormInputClassName, getToolFormTextareaClassName } from '../tool-form-styles';
import { resolveComposioToolLogo } from './composio-logos';
import { getComposioConfig, getToolEditSubject, getToolVisibility } from './tool-detail-utils';
import { APPROVAL_TO_VALUE, HumanApprovalField, toApprovalOption, type HumanApprovalOption } from './human-approval';

const TOOLKIT_SUGGESTIONS = [
  'OUTLOOK',
  'MICROSOFT_TEAMS',
  'GMAIL',
  'GITHUB',
  'SLACK',
  'NOTION',
  'GOOGLECALENDAR',
];

const CATALOG_RESULTS_CAP = 30;
const ACTION_RESULTS_CAP = 100;

const normalizeToolkitSlug = (value: string) => value.trim().toUpperCase();

const toolkitMatches = (toolkits: string[], slug: string) =>
  toolkits.some((toolkit) => normalizeToolkitSlug(toolkit) === normalizeToolkitSlug(slug));

type EditComposioToolModalProps = {
  mode: 'create' | 'edit';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tool?: {
    id: string;
    name: string;
    description: string;
    status?: string;
    visibility?: string | null;
    created_by?: string;
    CreatedBy?: string;
  };
  isBlocksTool?: boolean;
};

type ComposioFormData = {
  toolName: string;
  description: string;
  visibility: ResourceVisibility;
  isOpenTool: boolean;
  status: boolean;
  humanApproval: HumanApprovalOption;
};

function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-3', className)}>
      <div className="space-y-0.5">
        <h3 className="text-[13px] font-medium leading-6">{title}</h3>
        {description ? (
          <p className="text-xs leading-5 text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function AppCatalogRow({
  entry,
  isSelected,
  onSelect,
  customAuthLabel,
}: {
  entry: IComposioCatalogToolkit;
  isSelected: boolean;
  onSelect: () => void;
  customAuthLabel: string;
}) {
  const logo = resolveComposioToolLogo(entry.slug) || entry.logo;

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border px-2.5 py-2 text-left transition-colors',
        isSelected
          ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
          : 'border-transparent hover:border-border hover:bg-muted/50'
      )}
      onClick={onSelect}
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          className="h-8 w-8 shrink-0 rounded-md border bg-background object-contain p-0.5"
          loading="lazy"
        />
      ) : (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-muted text-[10px] font-semibold uppercase text-muted-foreground">
          {entry.name.slice(0, 2)}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium leading-5">{entry.name}</span>
        <span className="block truncate text-xs text-muted-foreground">{entry.slug}</span>
      </span>
      {!entry.composioManagedAuth && !entry.noAuth ? (
        <Badge variant="outline" className="shrink-0 text-[10px]">
          {customAuthLabel}
        </Badge>
      ) : null}
      {isSelected ? (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" aria-hidden />
        </span>
      ) : (
        <span className="h-5 w-5 shrink-0 rounded-full border border-border" aria-hidden />
      )}
    </button>
  );
}

function ToolkitSlugPicker({
  toolkit,
  selectedSlugs,
  onToggleSlug,
  onSelectAllSlugs,
  authConfigOverride: _authConfigOverride,
  onAuthConfigOverrideChange: _onAuthConfigOverrideChange,
  defaultExpanded = false,
  disabled = false,
}: {
  toolkit: string;
  selectedSlugs: string[];
  onToggleSlug: (slug: string, checked: boolean) => void;
  onSelectAllSlugs: (toolkit: string, slugs: string[]) => void;
  authConfigOverride: string;
  onAuthConfigOverrideChange: (value: string) => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [search, setSearch] = useState('');
  const slugsQuery = useGetComposioToolkitSlugs(toolkit, { enabled: expanded });

  const selectedForToolkit = useMemo(
    () => selectedSlugs.filter((slug) => slug.startsWith(`${toolkit}_`) || slug === toolkit),
    [selectedSlugs, toolkit]
  );

  const filtered = useMemo(() => {
    const items = slugsQuery.data ?? [];
    const term = search.trim().toLowerCase();
    const matching = term
      ? items.filter(
        (item) =>
          item.slug.toLowerCase().includes(term) ||
          (item.name ?? '').toLowerCase().includes(term) ||
          (item.description ?? '').toLowerCase().includes(term)
      )
      : items;
    return matching.slice(0, ACTION_RESULTS_CAP);
  }, [slugsQuery.data, search]);

  const allSlugs = useMemo(
    () => (slugsQuery.data ?? []).map((item) => item.slug),
    [slugsQuery.data]
  );
  const allSelected = allSlugs.length > 0 && allSlugs.every((slug) => selectedSlugs.includes(slug));
  const someSelected = selectedForToolkit.length > 0 && !allSelected;

  const statusLabel =
    selectedForToolkit.length > 0
      ? t('ACTIONS_SELECTED', {
        defaultValue: '{{count}} action(s) selected',
        count: selectedForToolkit.length,
      })
      : t('ALL_ACTIONS', { defaultValue: 'All actions' });

  const handleSelectAllChange = (checked: boolean) => {
    if (disabled) return;
    if (checked) {
      onSelectAllSlugs(toolkit, allSlugs);
    } else {
      onSelectAllSlugs(toolkit, []);
    }
  };

  return (
    <Collapsible
      open={expanded}
      onOpenChange={setExpanded}
      className="overflow-hidden rounded-lg border bg-background"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40"
        >
          <span className="flex min-w-0 items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate text-[13px] font-medium">{toolkit}</span>
          </span>
          <Badge
            variant={selectedForToolkit.length > 0 ? 'secondary' : 'outline'}
            className="shrink-0 text-[10px] font-normal dark:text-[#ffffff] text-[#000000]"
          >
            {statusLabel}
          </Badge>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 overflow-visible border-t bg-muted/20 px-3 py-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('SEARCH_ACTIONS', { defaultValue: 'Search actions...' })}
            disabled={disabled}
            className="h-8 border-border/60 bg-background pl-8 text-[13px]"
          />
        </div>
        {slugsQuery.isLoading && (
          <div className="flex items-center gap-2 py-3 text-[13px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('LOADING_ACTIONS', { defaultValue: 'Loading actions...' })}
          </div>
        )}
        {slugsQuery.isError && (
          <p className="py-2 text-[13px] text-destructive">
            {t('FAILED_TO_LOAD_ACTIONS', {
              defaultValue: 'Failed to load actions for this app.',
            })}
          </p>
        )}
        {!slugsQuery.isLoading && !slugsQuery.isError && filtered.length === 0 && (
          <p className="py-2 text-[13px] text-muted-foreground">
            {t('NO_ACTIONS_FOUND', { defaultValue: 'No actions found.' })}
          </p>
        )}
        {allSlugs.length > 0 && !slugsQuery.isLoading && !slugsQuery.isError && (
          <label
            className={cn(
              'flex items-center gap-2.5 rounded-md px-1 py-1.5',
              disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
            )}
          >
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={(value) => handleSelectAllChange(value === true)}
              disabled={disabled}
            />
            <span className="text-[13px] font-medium leading-5">
              {t('SELECT_ALL', { defaultValue: 'Select all' })}
            </span>
          </label>
        )}
        {filtered.length > 0 && (
          <div className="space-y-0.5 rounded-md border border-border bg-background dark:bg-card/50 p-1 pb-3">
            {filtered.map((item) => {
              const checked = selectedSlugs.includes(item.slug);
              return (
                <label
                  key={item.slug}
                  className={cn(
                    'flex items-start gap-2.5 rounded-md px-2 py-2 transition-colors',
                    disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-muted/60 dark:hover:bg-muted/40',
                    checked && 'bg-primary/10 dark:bg-primary/20'
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(value) => onToggleSlug(item.slug, value === true)}
                    disabled={disabled}
                    className="mt-0.5"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium leading-5 text-foreground">
                      {item.name || item.slug}
                    </span>
                    {item.description ? (
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
                        {item.description}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {/* <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          ...
        </Collapsible> */}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function EditComposioToolModal({
  mode,
  open,
  onOpenChange,
  tool,
  isBlocksTool = false,
}: EditComposioToolModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY;
  const { mutateAsync: saveComposioTool, isPending } = useSaveComposioTool();

  const isEdit = mode === 'edit';
  const { data: toolDetails } = useGetToolById(
    { toolId: tool?.id || '', projectKey },
    { enabled: open && isEdit && !!tool?.id && !!projectKey }
  );
  const isOwner = useIsOwner(getToolEditSubject(toolDetails, tool));
  const canEdit = !isEdit || isOwner;

  const [toolkits, setToolkits] = useState<string[]>([]);
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>([]);
  const [limitInput, setLimitInput] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [showAppPicker, setShowAppPicker] = useState(true);
  const [authConfigOverrides, setAuthConfigOverrides] = useState<Record<string, string>>({});

  const selectedToolkit = toolkits[0] ?? null;

  const catalogQuery = useGetComposioToolkitCatalog({ enabled: open });
  const catalogBySlug = useMemo(() => {
    const map = new Map<string, IComposioCatalogToolkit>();
    for (const entry of catalogQuery.data?.toolkits ?? []) {
      map.set(normalizeToolkitSlug(entry.slug), entry);
    }
    return map;
  }, [catalogQuery.data]);

  const catalogResults = useMemo(() => {
    const catalog = catalogQuery.data?.toolkits ?? [];
    const term = catalogSearch.trim().toLowerCase();
    const matching = term
      ? catalog.filter(
        (entry) =>
          entry.slug.toLowerCase().includes(term) || entry.name.toLowerCase().includes(term)
      )
      : catalog;

    const sorted = [...matching].sort((a, b) => {
      const aSelected = toolkitMatches(toolkits, a.slug) ? 0 : 1;
      const bSelected = toolkitMatches(toolkits, b.slug) ? 0 : 1;
      return aSelected - bSelected;
    });

    return sorted.slice(0, CATALOG_RESULTS_CAP);
  }, [catalogQuery.data, catalogSearch, toolkits]);

  const selectedCatalogEntry = selectedToolkit
    ? catalogBySlug.get(normalizeToolkitSlug(selectedToolkit))
    : undefined;
  const selectedToolkitLogo =
    resolveComposioToolLogo(selectedToolkit) || selectedCatalogEntry?.logo || null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<ComposioFormData>({
    mode: 'onChange',
    defaultValues: {
      toolName: '',
      description: '',
      visibility: DEFAULT_RESOURCE_VISIBILITY,
      isOpenTool: false,
      status: false,
      humanApproval: 'auto',
    },
  });

  useEffect(() => {
    if (!open) return;

    if (!isEdit) {
      reset({
        toolName: '',
        description: '',
        visibility: DEFAULT_RESOURCE_VISIBILITY,
        isOpenTool: false,
        status: false,
        humanApproval: 'auto',
      });
      setToolkits([]);
      setSelectedSlugs([]);
      setLimitInput('');
      setAuthConfigOverrides({});
      setCatalogSearch('');
      setShowAppPicker(true);
      return;
    }

    const detail = toolDetails?.tool ?? toolDetails?.data;
    const config = getComposioConfig(detail ?? (toolDetails as never));
    reset({
      toolName: detail?.name || tool?.name || '',
      description: detail?.description || tool?.description || '',
      visibility: getToolVisibility(detail ?? tool),
      isOpenTool: detail?.IsOpenTool ?? detail?.is_open_tool ?? isBlocksTool,
      status: (detail?.status || tool?.status) === 'active',
      humanApproval: toApprovalOption(detail?.RequiresApproval ?? toolDetails?.RequiresApproval),
    });
    setToolkits(config.toolkits.slice(0, 1));
    setSelectedSlugs(config.tools ?? []);
    setLimitInput(config.limit != null ? String(config.limit) : '');
    setAuthConfigOverrides(config.authConfigIds);
    setShowAppPicker(config.toolkits.length === 0);
  }, [open, isEdit, toolDetails, tool, reset, isBlocksTool]);

  const isBlocksToolChecked = watch('isOpenTool');
  const toolName = watch('toolName');
  const description = watch('description');
  const hasSlugSelection = selectedSlugs.length > 0;
  const canSave =
    Boolean(toolName?.trim()) && Boolean(description?.trim()) && toolkits.length > 0;

  const syncToolkits = (normalized: string[]) => {
    const next = normalized.slice(0, 1);
    setToolkits(next);
    setSelectedSlugs((current) =>
      current.filter((slug) => next.some((toolkit) => slug.startsWith(`${toolkit}_`)))
    );
    setShowAppPicker(next.length === 0);
  };

  const handleSelectApp = (toolkit: string) => {
    const normalized = normalizeToolkitSlug(toolkit);
    if (!normalized) return;

    if (toolkitMatches(toolkits, normalized)) {
      syncToolkits([]);
      return;
    }

    syncToolkits([normalized]);
    setShowAppPicker(false);
    setCatalogSearch('');
  };

  const handleToggleSlug = (slug: string, checked: boolean) => {
    setSelectedSlugs((current) =>
      checked ? Array.from(new Set([...current, slug])) : current.filter((item) => item !== slug)
    );
  };

  const handleSelectAllSlugs = (toolkit: string, slugs: string[]) => {
    setSelectedSlugs((current) => {
      const withoutToolkit = current.filter(
        (slug) => !slug.startsWith(`${toolkit}_`) && slug !== toolkit
      );
      return slugs.length > 0 ? [...withoutToolkit, ...slugs] : withoutToolkit;
    });
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset();
      setToolkits([]);
      setSelectedSlugs([]);
      setLimitInput('');
      setAuthConfigOverrides({});
      setCatalogSearch('');
      setShowAppPicker(true);
    }
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: ComposioFormData) => {
    if (!canEdit) return;

    if (!projectKey) {
      toast({
        variant: 'destructive',
        title: t('MISSING_PROJECT_KEY', { defaultValue: 'Missing project key' }),
        description: t('UNABLE_TO_SAVE_COMPOSIO_TOOL', {
          defaultValue: 'Unable to save the connector tool for this tenant.',
        }),
      });
      return;
    }

    if (toolkits.length === 0) {
      toast({
        variant: 'destructive',
        title: t('CONNECTOR_APP_REQUIRED', { defaultValue: 'App required' }),
        description: t('ADD_AT_LEAST_ONE_CONNECTOR_APP', {
          defaultValue: 'Select an app, such as Gmail or GitHub.',
        }),
      });
      return;
    }

    const parsedLimit = limitInput.trim() ? Number(limitInput) : null;
    if (parsedLimit != null && (!Number.isInteger(parsedLimit) || parsedLimit < 1)) {
      toast({
        variant: 'destructive',
        title: t('INVALID_LIMIT', { defaultValue: 'Invalid limit' }),
        description: t('LIMIT_MUST_BE_POSITIVE', {
          defaultValue: 'Limit must be a positive whole number.',
        }),
      });
      return;
    }

    const cleanedOverrides = Object.fromEntries(
      Object.entries(authConfigOverrides)
        .filter(([toolkit, value]) => toolkits.includes(toolkit) && value.trim())
        .map(([toolkit, value]) => [toolkit, value.trim()])
    );

    try {
      const response = await saveComposioTool({
        ...(isEdit && tool?.id ? { id: tool.id } : {}),
        name: data.toolName.trim(),
        description: data.description.trim(),
        toolkits,
        tools: hasSlugSelection ? selectedSlugs : null,
        limit: hasSlugSelection ? null : parsedLimit,
        auth_config_ids: Object.keys(cleanedOverrides).length > 0 ? cleanedOverrides : null,
        project_key: projectKey,
        visibility: data.visibility,
        is_open_tool: isAdmin ? data.isOpenTool : false,
        requires_approval: APPROVAL_TO_VALUE[data.humanApproval],
        ...(isEdit && isAdmin && data.isOpenTool
          ? { status: (data.status ? 'active' : 'inactive') as ToolStatus }
          : {}),
      });

      if (!response.is_success) {
        throw new Error(
          response.message ||
          response.detail ||
          t('FAILED_TO_SAVE_COMPOSIO_TOOL', { defaultValue: 'Failed to save connector tool' })
        );
      }

      toast({
        variant: 'success',
        title: isEdit
          ? t('COMPOSIO_TOOL_UPDATED', { defaultValue: 'Connector tool updated' })
          : t('COMPOSIO_TOOL_CREATED', { defaultValue: 'Connector tool created' }),
        description: response.detail || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['tools'] });
      handleClose(false);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: isEdit
          ? t('UPDATE_FAILED', { defaultValue: 'Update failed' })
          : t('CREATE_FAILED', { defaultValue: 'Create failed' }),
        description:
          error instanceof Error
            ? error.message
            : t('FAILED_TO_SAVE_COMPOSIO_TOOL', { defaultValue: 'Failed to save connector tool.' }),
      });
    }
  };

  let saveLabel = t('CREATE', { defaultValue: 'Create' });
  if (isPending) saveLabel = t('SAVING', { defaultValue: 'Saving...' });
  else if (isEdit) saveLabel = t('SAVE', { defaultValue: 'Save' });

  const showCatalogPicker = showAppPicker || !selectedToolkit;

  const showCatalog =
    catalogQuery.isSuccess && (catalogQuery.data?.toolkits?.length ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-[640px] flex-col overflow-hidden rounded-lg border-0 p-0 shadow-xl sm:max-h-[min(90vh,900px)]">
        <DialogHeader className="shrink-0 space-y-1 border-b px-5 pb-4 pt-5 sm:px-6">
          <DialogTitle className="pr-8 text-lg font-semibold leading-snug sm:text-xl">
            {!isEdit
              ? t('ADD_CONNECTOR_TOOL', { defaultValue: 'Add connector tool' })
              : canEdit
                ? t('EDIT_CONNECTOR_TOOL', { defaultValue: 'Edit connector tool' })
                : t('VIEW_CONNECTOR_TOOL', { defaultValue: 'View connector tool' })}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            {t('CONNECTOR_TOOL_MODAL_DESCRIPTION', {
              defaultValue:
                'Connect a third-party app so your agent can take actions on your behalf. Users sign in with their own accounts when needed.',
            })}
          </DialogDescription>
        </DialogHeader>

        <form className="flex min-h-0 flex-1 flex-col overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 pb-8 pt-5 sm:px-6">
            {!canEdit ? (
              <WarningAlert>
                {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_TOOL') ||
                  'You are not the owner of this tool.'}
              </WarningAlert>
            ) : null}
            <FormSection title={`${t('TOOL_NAME') || 'Tool name'} *`}>
              <Input
                id="composioToolName"
                placeholder={t('ENTER_NAME') || 'Enter name'}
                {...register('toolName', {
                  required: t('TOOL_NAME_REQUIRED') || 'Tool name is required',
                  setValueAs: (value) => value.trim(),
                })}
                disabled={!canEdit || isPending}
                className={getToolFormInputClassName(!!errors.toolName)}
              />
              {errors.toolName ? (
                <p className="text-[13px] text-destructive">{errors.toolName.message}</p>
              ) : null}
            </FormSection>

            <Controller
              name="visibility"
              control={control}
              render={({ field }) => (
                <VisibilityField
                  value={field.value}
                  onValueChange={field.onChange}
                  idPrefix="composio-tool-visibility"
                  disabled={!canEdit || isPending}
                />
              )}
            />

            <FormSection title={`${t('DESCRIPTION') || 'Description'} *`}>
              <Textarea
                id="composioDescription"
                rows={3}
                placeholder={t('CONNECTOR_DESCRIPTION_PLACEHOLDER', {
                  defaultValue: 'What should this tool help the agent do?',
                })}
                {...register('description', {
                  required: t('DESCRIPTION_REQUIRED') || 'Description is required',
                  setValueAs: (value) => value.trim(),
                })}
                disabled={!canEdit || isPending}
                className={getToolFormTextareaClassName(!!errors.description)}
              />
              {errors.description ? (
                <p className="text-[13px] text-destructive">{errors.description.message}</p>
              ) : null}
            </FormSection>

            <HumanApprovalField
              control={control}
              name="humanApproval"
              id="composioHumanApproval"
              toolType="composio"
              disabled={!canEdit || isPending}
            />

            <FormSection
              title={`${t('CONNECTOR_APP', { defaultValue: 'App' })} *`}
              description={
                selectedToolkit && !showAppPicker
                  ? t('CONNECTOR_APP_SELECTED_HINT', {
                    defaultValue: 'This tool uses one connected app. Change it below if needed.',
                  })
                  : t('CONNECTOR_APP_HINT', {
                    defaultValue: 'Pick one app from the list below.',
                  })
              }
            >
              {selectedToolkit && !showAppPicker ? (
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2.5">
                  {selectedToolkitLogo ? (
                    <img
                      src={selectedToolkitLogo}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md border bg-background object-contain p-0.5"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border bg-background text-[11px] font-semibold uppercase">
                      {selectedToolkit.slice(0, 2)}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium leading-5">
                      {selectedCatalogEntry?.name ?? selectedToolkit}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{selectedToolkit}</p>
                  </div>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0"
                      onClick={() => setShowAppPicker(true)}
                    >
                      {t('CHANGE_APP', { defaultValue: 'Change app' })}
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {showCatalogPicker && showCatalog ? (
                <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={catalogSearch}
                      onChange={(event) => setCatalogSearch(event.target.value)}
                      placeholder={t('SEARCH_CONNECTOR_APPS', {
                        defaultValue: 'Search apps (Gmail, GitHub, Slack…)',
                      })}
                      disabled={!canEdit || isPending}
                      className="h-9 border-border/60 bg-background pl-8 text-[13px]"
                    />
                  </div>
                  <div className="space-y-1">
                    {catalogResults.map((entry) => (
                      <AppCatalogRow
                        key={entry.slug}
                        entry={entry}
                        isSelected={toolkitMatches(toolkits, entry.slug)}
                        onSelect={() => handleSelectApp(entry.slug)}
                        customAuthLabel={t('CUSTOM_AUTH_REQUIRED', { defaultValue: 'Custom auth' })}
                      />
                    ))}
                    {catalogResults.length === 0 ? (
                      <p className="px-2 py-4 text-center text-[13px] text-muted-foreground">
                        {t('NO_APPS_FOUND', { defaultValue: 'No apps found.' })}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : showCatalogPicker && catalogQuery.isLoading ? (
                <div className="flex items-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('LOADING_APPS', { defaultValue: 'Loading apps…' })}
                </div>
              ) : showCatalogPicker ? (
                <div className="flex flex-wrap gap-1.5">
                  {TOOLKIT_SUGGESTIONS.map((suggestion) => (
                    <Badge
                      key={suggestion}
                      variant={toolkitMatches(toolkits, suggestion) ? 'default' : 'outline'}
                      className="cursor-pointer text-xs text-[#ffffff]"
                      onClick={() => handleSelectApp(suggestion)}
                    >
                      {suggestion}
                    </Badge>
                  ))}
                </div>
              ) : null}
            </FormSection>

            {selectedToolkit ? (
              <FormSection
                title={t('ACTION_ALLOWLIST', { defaultValue: 'Action allowlist' })}
                description={t('ACTION_ALLOWLIST_HINT', {
                  defaultValue:
                    'Optional. Choose specific actions, or leave empty to use the most common ones automatically.',
                })}
              >
                <ToolkitSlugPicker
                  toolkit={selectedToolkit}
                  selectedSlugs={selectedSlugs}
                  onToggleSlug={handleToggleSlug}
                  onSelectAllSlugs={handleSelectAllSlugs}
                  authConfigOverride={authConfigOverrides[selectedToolkit] ?? ''}
                  onAuthConfigOverrideChange={(value) =>
                    setAuthConfigOverrides((current) => ({ ...current, [selectedToolkit]: value }))
                  }
                  defaultExpanded
                  disabled={!canEdit}
                />
              </FormSection>
            ) : null}

            {isAdmin ? (
              <FormSection
                title={t('ADMIN_OPTIONS', { defaultValue: 'Admin options' })}
                className="rounded-lg border bg-muted/20 p-3"
              >
                <div className="space-y-3">
                  <label className="flex cursor-pointer items-center gap-2.5">
                    <Controller
                      name="isOpenTool"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      )}
                    />
                    <span className="text-[13px] font-medium leading-6">
                      {t('BLOCKS_TOOL') || 'Blocks Tool'}
                    </span>
                  </label>

                  {isEdit && isBlocksToolChecked ? (
                    <label className="flex cursor-pointer items-center gap-2.5">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                          />
                        )}
                      />
                      <span className="text-[13px] font-medium leading-6">
                        {t('TOOL_ACTIVE') || 'Tool Active'}
                      </span>
                    </label>
                  ) : null}
                </div>
              </FormSection>
            ) : null}
          </div>

          <DialogFooter className="shrink-0 gap-2 border-t bg-background px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
            <Button
              type="button"
              variant="outline"
              className="h-9 px-4"
              onClick={() => handleClose(false)}
              disabled={isPending}
            >
              {t('CANCEL') || 'Cancel'}
            </Button>
            {canEdit ? (
              <Button
                type="submit"
                className="h-9 px-4"
                disabled={isPending || !canSave}
              >
                {saveLabel}
              </Button>
            ) : null}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
